<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { COUNTRIES_MAP } from '@/const/countries.const';
  import { isPlausibleOrdersWhatsapp } from '@/domain/store-setup/orders-setup';
  import type { IOrdersForm } from '@/domain/store-setup/orders-setup';
  import { setOrdersMode } from '@/domain/store-setup/orders-step';
  import type { TOrdersFormPatch } from '@/domain/store-setup/orders-step';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * Where orders arrive (plan §4.8 item 4): the panel by default, or WhatsApp
   * with the number orders go to. Both lines stay mounted (`v-show`), so the
   * number input is never unmounted while it has the focus.
   */
  const WHATSAPP_MAX_LENGTH = 32;

  const props = withDefaults(
    defineProps<{
      value: IOrdersForm;
      country: string;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TOrdersFormPatch): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string) => String(proxy.$t(key));

  const inputId = nextDomId('orders-whatsapp');
  const root = ref<HTMLElement | null>(null);
  /** The number was left once: its error may show. */
  const touched = ref(false);

  const whatsappMode = computed(() => props.value.mode === 'wa');
  const placeholder = computed(() => {
    const telCode = COUNTRIES_MAP.get(props.country)?.telCode;
    return telCode ? `+${telCode}` : undefined;
  });
  const error = computed(() =>
    whatsappMode.value &&
    touched.value &&
    !isPlausibleOrdersWhatsapp(props.value.whatsapp, props.country)
      ? tr('storeSetup.orders.whatsappInvalid')
      : undefined
  );

  async function toWhatsapp() {
    if (props.disabled) return;
    emit('patch', setOrdersMode('wa'));
    // A number prefilled from the account is checked at once.
    touched.value = props.value.whatsapp.trim().length > 0;
    await nextTick();
    const input = root.value?.querySelector(`#${inputId}`);
    if (input instanceof HTMLInputElement) input.focus();
  }

  function toPanel() {
    if (!props.disabled) emit('patch', setOrdersMode('native'));
  }

  function onNumber(text: string) {
    if (!props.disabled) emit('patch', { whatsapp: text });
  }
</script>

<template>
  <div ref="root" class="v2-setup-orders-arrival">
    <div v-show="!whatsappMode" class="arrival-panel">
      <p class="arrival-line">
        <PosUiIcon name="cart-outline" class="arrival-icon" />
        <span>{{ $t('storeSetup.orders.arrive') }}</span>
      </p>
      <button
        type="button"
        class="arrival-link"
        :disabled="disabled"
        @click="toWhatsapp"
      >
        {{ $t('storeSetup.orders.whatsappLink') }}
      </button>
    </div>
    <div v-show="whatsappMode" class="arrival-whatsapp">
      <p class="arrival-line">
        <PosUiIcon name="whatsapp" class="arrival-icon" />
        <span>{{ $t('storeSetup.orders.whatsappArrive') }}</span>
      </p>
      <PosUiField
        class="whatsapp-field"
        :label="$t('storeSetup.orders.whatsappLabel')"
        :hint="$t('storeSetup.orders.whatsappHint')"
        :error="error"
        :input-id="inputId"
      >
        <PosUiInput
          :id="inputId"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          :value="value.whatsapp"
          :placeholder="placeholder"
          :maxlength="WHATSAPP_MAX_LENGTH"
          :disabled="disabled"
          @input="onNumber"
          @blur="touched = true"
        />
      </PosUiField>
      <button
        type="button"
        class="arrival-link"
        :disabled="disabled"
        @click="toPanel"
      >
        {{ $t('storeSetup.orders.whatsappBack') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-orders-arrival {
    @apply tw-flex tw-flex-col tw-px-1;
    .arrival-panel,
    .arrival-whatsapp {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-2;
    }
    .arrival-line {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-700;
    }
    .arrival-icon {
      @apply tw-shrink-0 tw-text-lg tw-text-neutral-500;
    }
    .whatsapp-field {
      @apply tw-w-full tw-max-w-sm;
    }
    .arrival-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-0 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4 hover:tw-text-neutral-700;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
